import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';

import type { FaqDto } from '@/services/types/api.types';

export interface FaqState {
  faqs: FaqDto[];
  itemLoadingStates: Set<number>;
}

export const useFaqStore = defineStore({
  id: 'faqs',
  state: (): FaqState => ({
    faqs: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (projectModuleId: number) => state.itemLoadingStates.has(projectModuleId),
    orderedFaqs: (state) => {
      return [...state.faqs].sort((a, b) => a.order_num - b.order_num);
    },
  },
  actions: {
    async fetchFaqsByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<FaqDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/frequently-asked-questions`,
        );
        this.faqs = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    createFaq(projectModuleId: number): FaqDto {
      return {
        project_module_id: projectModuleId,
        question: { en: '', fr: '' },
        answer: { en: '', fr: '' },
        order_num: -1,
      };
    },
    async saveFaq(faq: FaqDto) {
      try {
        if (faq.id) {
          await apiService.put<FaqDto>(`${Config.api.v2}/frequently-asked-questions/${faq.id}`, faq);
        } else {
          await apiService.post<FaqDto>(`${Config.api.v2}/frequently-asked-questions`, faq);
        }
        await this.fetchFaqsByModuleId(faq.project_module_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteFaq(faq: FaqDto) {
      try {
        await apiService.delete(`${Config.api.v2}/frequently-asked-questions/${faq.id!}`);
        await this.fetchFaqsByModuleId(faq.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },
    async updateFaqs(...faqs: FaqDto[]) {
      try {
        for (const faq of faqs) {
          await apiService.put(`${Config.api.v2}/frequently-asked-questions/${faq.id}`, faq);
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
});
